/**
 * How a URL becomes the trail of crumbs above the page.
 *
 * Lifted out of MainLayout so the rules can be tested without mounting the
 * whole app shell: the labelling is all string work, and the cases that have
 * gone wrong (dead links, raw internal ids) are all about which segment gets
 * which treatment.
 */
import type { BreadcrumbItem } from '@lognai/synapse-design-system-v2';
import type { NavItemConfig } from '@lognai/frontend-core';

/** Dataset details share the list route; upload/review views are separate contexts. */
export function getDatasetDetailId(pathname: string, search: string): string | undefined {
  if (pathname.replace(/\/$/, '') !== '/analytics/datasets') return undefined;
  const params = new URLSearchParams(search);
  if (params.has('new') || params.has('import')) return undefined;
  return params.get('dataset') || undefined;
}

/**
 * Path segments that are an internal id rather than something to read.
 *
 * The fourth alternative is the platform's OWN id format — `generateId()`
 * returns `<epoch ms>-<random>` (core-platform/src/utils/id-generator.ts), and
 * the seeds use the same shape — which nothing here matched. Every detail
 * route whose id is not resolved to a label below therefore rendered the raw
 * id title-cased into the breadcrumb: "1770227570970 Crkd4cihz" (QA ledger
 * LC-006). It reads as "Details" now, and as the record's own reference
 * wherever a resolver exists.
 */
export const ID_SEGMENT =
  /^[0-9a-f]{20,}$|^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$|^\d+$|^\d{10,}-[0-9a-z]{4,}$/i;

const BREADCRUMB_ROOT_LABELS: Record<string, string> = {
  analytics: 'Analytics',
  'freight-audit': 'Freight Audit',
  invoices: 'Invoices',
};

// Segments that are acronyms read wrong title-cased ("Freight Rfq").
const ACRONYMS: Record<string, string> = {
  ai: 'AI', cbe: 'CBE', dd: 'D&D', erp: 'ERP', hs: 'HS', kpi: 'KPI', kpis: 'KPIs',
  po: 'PO', pos: 'POs', rfq: 'RFQ', rfqs: 'RFQs', trf: 'TRF',
};

function humanizeSegment(segment: string): string {
  return segment
    .split(/[-_]/)
    .map((word) => ACRONYMS[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function safeDecodeURIComponent(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

// Flattens nav items (including grouped sub-items) into path -> label, so
// breadcrumb segments that match a registered route use its real nav label
// instead of a naively humanized path segment.
function flattenNavPaths(
  items: NavItemConfig[],
  map = new Map<string, string>()
): Map<string, string> {
  for (const item of items) {
    if (item.path && !item.isSeparator && !item.isGroupHeader) {
      map.set(item.path, item.label);
    }
    if (item.children?.length) flattenNavPaths(item.children, map);
  }
  return map;
}

// Dynamic detail-route id segments aren't resolvable to a human label from the
// route registry alone. `entityId`/`entityLabel` (resolved via the same
// entity-label registry the AI chat context uses, see deriveAiPageContext
// below) override the id segment when available; otherwise it collapses to a
// generic "Details" crumb.

/**
 * True when `path` matches a REGISTERED route pattern (exact segments, with
 * `:param` — including optional `:param?` — wildcards).
 *
 * Breadcrumb links must pass this check before becoming clickable: crumbs are
 * derived from raw path segments, and many cumulative prefixes are not routes
 * at all (`/orders`, `/inventory`, `/warehouse/.../workflows`, …). Linking
 * them unconditionally sent users to the Not-Found page from ELEVEN modules'
 * detail screens — the products case was just the first one reported. An
 * unresolvable segment now renders as plain text instead of a dead link.
 */
function isRegisteredRoutePath(path: string, routePatterns: string[][]): boolean {
  const segs = path.split('/').filter(Boolean);
  return routePatterns.some((pattern) => {
    // Optional trailing params (e.g. `execute/:taskId?`) may be omitted.
    const required = pattern.filter((s, i) => !(s.endsWith('?') && i >= segs.length));
    if (required.length !== segs.length) return false;
    return required.every((s, i) => s.startsWith(':') || s === segs[i]);
  });
}

/**
 * True when the route that matches `path` declares this final segment as a
 * `:param` — i.e. the segment is an id because the ROUTE says so, whatever it
 * happens to look like.
 *
 * ID_SEGMENT has to guess id-ness from shape, and it only knows the shapes this
 * codebase generates (uuid, `<ts>-<suffix>`, ObjectId, numeric). A record whose
 * id is none of those falls through to humanizeSegment and its raw id is
 * title-cased into a customer-facing trail: a staging vendor application keyed
 * `mock-review-flow-0001` billed itself as "Mock Review Flow 0001" in the
 * breadcrumb of a real review screen (QA ledger VO2-002).
 *
 * The route pattern already knows. `/vendor-onboarding/applications/:id` says
 * that segment is an id without inspecting it, so consult that first and keep
 * the shape check only for paths no registered pattern covers.
 */
function isRouteParamSegment(path: string, routePatterns: string[][]): boolean {
  const segs = path.split('/').filter(Boolean);
  if (segs.length === 0) return false;
  const lastSegmentKinds = routePatterns.flatMap((pattern) => {
    const required = pattern.filter((s, i) => !(s.endsWith('?') && i >= segs.length));
    if (required.length !== segs.length) return [];
    if (!required.every((s, i) => s.startsWith(':') || s === segs[i])) return [];
    return [required[segs.length - 1]!.startsWith(':') ? 'param' : 'static'];
  });
  // A static route wins over a sibling `:param`, exactly as the router resolves
  // it: `/shipments/containers` is the Containers page even though
  // `/shipments/:id` also matches it.
  return lastSegmentKinds.includes('param') && !lastSegmentKinds.includes('static');
}

/**
 * A route that declared a `parentPath`, with its own pattern split into
 * segments and the full path of the parent it hangs under.
 */
export interface RouteParent {
  pattern: string[];
  parentPath: string;
}

export interface BreadcrumbOptions {
  /**
   * Plugins mounted under their own prefix whose pages belong to a rail
   * SECTION: first path segment → the section's name. The prefix is not a page
   * (`/temporary-permits` is the plugin's mount, not "Temporary Permits"), so it renders as the section
   * crumb — "Home › Transport › Requests" — and is never a link, because a
   * section is a rail entry that groups pages rather than a page of its own.
   */
  sectionRoots?: Record<string, string>;
  /**
   * Routes that sit under another route by declaration. The URL of a detail
   * page need not repeat its list page's segment — a permit is
   * `/temporary-permits/cases/:id` but its list is `/temporary-permits/register` — so the crumb between
   * the section and the record would otherwise be the humanized URL segment
   * ("Cases") rather than the page the user came from ("Permits and licences").
   */
  routeParents?: RouteParent[];
}

const SECTION_ENTITY_PATTERNS: Array<[RegExp, { pluginId: string; entityType: string }]> = [
  [/^\/temporary-permits\/requests\/([^/]+)/, { pluginId: 'temporary-permits', entityType: 'request' }],
  [/^\/temporary-permits\/cases\/([^/]+)/, { pluginId: 'temporary-permits', entityType: 'case' }],
  [/^\/temporary-permits\/guarantees\/([^/]+)/, { pluginId: 'temporary-permits', entityType: 'guarantee' }],
  [/^\/temporary-permits\/assets\/([^/]+)/, { pluginId: 'temporary-permits', entityType: 'asset' }],
];

/**
 * The record a sectioned plugin's detail page is about, for the crumb label
 * ONLY — deliberately not folded into the AI page context, which would hand
 * the chat an entity type its backend has no context builder for.
 */
export function deriveSectionEntity(
  pathname: string,
  isStaticSegment: (pathname: string, segment: string) => boolean
): { pluginId: string; entityType: string; entityId: string } | undefined {
  for (const [re, meta] of SECTION_ENTITY_PATTERNS) {
    const id = pathname.match(re)?.[1];
    // `/temporary-permits/requests/new` is a page, not a record.
    if (id && id !== 'new' && !isStaticSegment(pathname, id)) return { ...meta, entityId: id };
  }
  return undefined;
}

/** The parent a non-route path prefix stands under, if some route below it declared one. */
function parentOfPrefix(path: string, routeParents: RouteParent[]): string | undefined {
  const segs = path.split('/').filter(Boolean);
  return routeParents.find(
    (r) => r.pattern.length > segs.length && segs.every((s, i) => r.pattern[i] === s)
  )?.parentPath;
}

export function buildBreadcrumbs(
  pathname: string,
  navItems: NavItemConfig[],
  navigate: (path: string) => void,
  routePatterns: string[][],
  entityId?: string,
  entityLabel?: string | null,
  options: BreadcrumbOptions = {}
): BreadcrumbItem[] {
  if (pathname === '/' || pathname === '/dashboard') return [];
  const { sectionRoots = {}, routeParents = [] } = options;
  const navLabels = flattenNavPaths(navItems);
  const segments = pathname.split('/').filter(Boolean);
  const isAnalyticsDataset =
    segments[0] === 'analytics' && segments[1] === 'datasets' && segments.length === 3;
  const isAnalyticsTheme =
    segments.length === 3 && segments[0] === 'analytics' && segments[1] === 'themes' && segments[2] !== 'new';
  const isAnalyticsDashboard =
    segments.length === 3 && segments[0] === 'analytics' && segments[1] === 'dashboards';
  const crumbs: BreadcrumbItem[] = [{ label: 'Home', onClick: () => navigate('/dashboard') }];
  const sectionLabel = segments[0] ? sectionRoots[segments[0]] : undefined;
  let acc = '';
  segments.forEach((segment, i) => {
    acc += `/${segment}`;
    const isLast = i === segments.length - 1;
    const path = acc;
    if (i === 0 && sectionLabel) {
      crumbs.push({ label: sectionLabel });
      // The plugin's own landing page IS the mount path, so with nothing after
      // the prefix the page itself still needs its crumb.
      if (isLast) crumbs.push({ label: navLabels.get(path) ?? 'Dashboard' });
      return;
    }
    // Dashboard detail trails identify the dashboard directly under Analytics.
    if (isAnalyticsDashboard && i === 1) return;
    let label: string;
    let linkPath = path;
    const parentPath = sectionLabel ? parentOfPrefix(path, routeParents) : undefined;
    if (isAnalyticsDashboard && isLast) {
      label = entityId === segment ? entityLabel || 'Dashboard' : 'Dashboard';
    } else if (isAnalyticsTheme && isLast) {
      label = entityId === segment ? entityLabel || 'Theme' : 'Theme';
    } else if (isAnalyticsDataset && isLast) {
      label = entityId === segment ? entityLabel || 'Dataset' : 'Dataset';
    } else if (entityId && segment === entityId) {
      label = entityLabel || 'Details';
    } else if (ID_SEGMENT.test(segment) || isRouteParamSegment(path, routePatterns)) {
      label = 'Details';
    } else if (i === 0 && BREADCRUMB_ROOT_LABELS[segment]) {
      label = BREADCRUMB_ROOT_LABELS[segment];
    } else if (parentPath && !isRegisteredRoutePath(path, routePatterns)) {
      label = navLabels.get(parentPath) ?? humanizeSegment(safeDecodeURIComponent(segment));
      linkPath = parentPath;
    } else {
      label = navLabels.get(path) ?? humanizeSegment(safeDecodeURIComponent(segment));
    }
    const clickable = !isLast && isRegisteredRoutePath(linkPath, routePatterns);

    // A module whose landing route carries the module's own name renders the
    // name twice — "Home › Inventory › Inventory › Create" (QA ledger INV-010).
    // The repeat says nothing, and dropping it keeps the LATER crumb, which is
    // the deeper path and therefore the one worth linking.
    if (
      !((isAnalyticsDashboard || isAnalyticsDataset || isAnalyticsTheme) && isLast) &&
      crumbs[crumbs.length - 1]?.label === label
    )
      crumbs.pop();

    crumbs.push(clickable ? { label, onClick: () => navigate(linkPath) } : { label });
  });
  return crumbs;
}
